import blankVideoSrc from '../../videos/blank.mp4';

/* TODO: Don't forget to remove eslint-disable and verify code during your work */
/* eslint-disable */
export default class MediaControl {
  /// Holds one single IntersectionObserver for all videos on the page
  static observer;

  /// Set's the minimum threshold for videos that need play/pause controls for accessibility
  static a11yThreshold = 5;

  /// Reduced motion media query object
  static reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  /// The status of the device's low power mode setting
  static automaticPlaybackDisabled;

  /// Class selector constants
  static selectors = {
    buttonClass: 'c-media-control',
    labelClass: 'c-media-control__label',
    hiddenClass: 'c-media-control--hidden',
    fadedClass: 'c-media-control--faded',
    requiredClass: 'c-media-control--required',
    hostClass: 'c-media-control-host',
    hostAtViewportTopClass: 'c-media-control-host--at-viewport-top',
  };

  static blockSelectors = [
    '.c-hero-landing',
    '.media-full-width',
    '.c-media-with-text',
  ];

  static stickyHosts = [];

  static stickyOffsetScheduled = false;

  static stickyOffsetTrackingInitialized = false;

  /**
   * play() may reject if pause() runs before the promise settles (e.g. Swiper + IntersectionObserver).
   * @param {unknown} error
   * @returns {boolean}
   */
  static shouldIgnorePlayError(error) {
    if (!error || typeof error !== 'object') {
      return false;
    }
    if (error.name === 'AbortError') {
      return true;
    }
    const message = error.message;
    if (typeof message !== 'string') {
      return false;
    }
    if (message.includes('interrupted')) {
      return true;
    }
    if (message.includes('The operation was aborted')) {
      return true;
    }
    return false;
  }

  /// Alias for MediaControl.reducedMotionMQ.matches
  static get reducedMotion() {
    return MediaControl.reducedMotionMQ.matches;
  }

  /**
   * Creates the global IntersectionObserver instance for all videos and calls
   * createInstances when the page is ready.
   */
  static async init() {
    if (MediaControl.automaticPlaybackDisabled === undefined) {
      MediaControl.automaticPlaybackDisabled = await MediaControl.testAutomaticPlayback();
    }

    MediaControl.observer = new IntersectionObserver(
      MediaControl.intersectionCallback,
      { threshold: 0 },
    );

    MediaControl.initStickyOffsetTracking();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', MediaControl.createInstances);
    } else {
      MediaControl.createInstances();
    }
  }

  /**
   * Checks for the ability to autoplay videos without user interaction
   * @returns {Promise}
   */
  static async testAutomaticPlayback() {
    const testVideo = document.createElement('video');
    const source = document.createElement('source');

    testVideo.playsInline = true;
    testVideo.muted = true;
    testVideo.style.visibility = 'hidden';
    source.src = blankVideoSrc;
    source.type = 'video/mp4';

    testVideo.appendChild(source);
    document.documentElement.appendChild(testVideo);

    try {
      await testVideo.play();
      return false;
    } catch {
      return true;
    } finally {
      testVideo.remove();
    }
  }

  /**
   * Callback for IntersectionObserver that handles play/pause logic while the
   * user scrolls.
   * @param {Array<IntersectionObserverEntry>} entries
   */
  static intersectionCallback(entries) {
    entries.forEach(async (entry) => {
      const video = entry.target.observedVideo;
      const { mediaControl } = video;

      if (!mediaControl) {
        return;
      }

      mediaControl.isIntersecting = entry.isIntersecting;

      const {
        automaticPlaybackDisabled,
        autoplay,
        canPlay,
        isIntersecting,
        swiperSlideIsVisible,
        userPaused,
        userPlay,
      } = mediaControl;

      const { ended, paused } = video;

      const { reducedMotion } = MediaControl;

      // Paused by the system and not user input
      const systemPaused = paused && !userPaused && !ended;

      // Autoplay is enabled and should be honored
      const shouldAutoplay = autoplay && !reducedMotion;

      // Either autoplay or resume user-initiated play
      const shouldPlay = shouldAutoplay || userPlay;

      if (automaticPlaybackDisabled) {
        return;
      }

      const leaveViewport = !isIntersecting;
      const leaveSwiperSlide = Boolean(mediaControl.isSwiperSlide) && !swiperSlideIsVisible;

      if ((leaveViewport || leaveSwiperSlide) && !paused) {
        video.pause();
      } else if (
        isIntersecting
        && systemPaused
        && canPlay
        && shouldPlay
        && swiperSlideIsVisible
      ) {
        try {
          await video.play();
        } catch (error) {
          if (!MediaControl.shouldIgnorePlayError(error)) {
            console.warn('Playback failed', error.message);
          }
        }
      }
    });
  }

  static initStickyOffsetTracking() {
    if (MediaControl.stickyOffsetTrackingInitialized) {
      return;
    }

    MediaControl.stickyOffsetTrackingInitialized = true;

    window.addEventListener('scroll', MediaControl.scheduleStickyOffsetUpdate, { passive: true });
    window.addEventListener('resize', MediaControl.scheduleStickyOffsetUpdate);

    if (window.acf) {
      window.acf.addAction('render_block_preview', () => {
        MediaControl.registerStickyHosts();
      });
    }
  }

  static scheduleStickyOffsetUpdate = () => {
    if (MediaControl.stickyOffsetScheduled) {
      return;
    }

    MediaControl.stickyOffsetScheduled = true;

    requestAnimationFrame(() => {
      MediaControl.stickyOffsetScheduled = false;
      MediaControl.updateStickyOffsets();
    });
  };

  static registerStickyHosts() {
    const { hostClass } = MediaControl.selectors;
    const hosts = document.querySelectorAll(`.${hostClass}`);

    hosts.forEach((host) => {
      if (!host.querySelector(`.${MediaControl.selectors.buttonClass}`)) {
        return;
      }

      if (!MediaControl.stickyHosts.includes(host)) {
        MediaControl.stickyHosts.push(host);
      }
    });

    MediaControl.updateStickyOffsets();
  }

  static findBlockRoot(host) {
    return host.closest(MediaControl.blockSelectors.join(', '));
  }

  static updateStickyOffsets() {
    const { hostAtViewportTopClass } = MediaControl.selectors;

    MediaControl.stickyHosts.forEach((host) => {
      const block = MediaControl.findBlockRoot(host);

      if (!block) {
        host.classList.remove(hostAtViewportTopClass);
        return;
      }

      host.classList.toggle(
        hostAtViewportTopClass,
        block.getBoundingClientRect().top <= 0,
      );
    });
  }

  /**
   * Factory method for creating and instance of MediaControl and adding it
   * to the static IntersectionObserver instance.
   */
  static createInstances() {
    const buttons = document.querySelectorAll(`.${MediaControl.selectors.buttonClass}`);
    buttons.forEach((button) => {
      const videoId = button.dataset.videoId.replaceAll('/', '\\/');

      if (!videoId) {
        return;
      }

      const videos = document.querySelectorAll(`video#${videoId}, #${videoId} video`);

      if (videos.length === 1) {
        const instance = new MediaControl(button, videos[0]);
        MediaControl.observer.observe(instance.observerTarget);
      } else if (videos.length > 1) {
        const videoLoadPromises = [...videos].map((video) => new Promise((resolve) => {
          if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
            video.addEventListener('loadedmetadata', () => {
              resolve();
            });
          } else {
            resolve();
          }
        }));

        Promise.all(videoLoadPromises).then(() => {
          const sorted = [...videos].sort((a, b) => b.duration - a.duration);

          const instance = new MediaControl(button, sorted[0], sorted.slice(1));
          MediaControl.observer.observe(instance.observerTarget);
        });
      }
    });

    MediaControl.registerStickyHosts();
  }

  /**
   * Guesses the mime type of a video from the filename extension.
   * @param {String} filename
   */
  static guessMimeFromFilename(filename) {
    const ext = filename.split('.').pop();

    switch (ext) {
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'webm':
        return 'video/webm';
      default:
        return false;
    }
  }

  /// Updates the media button's required class when requiredControls is updated
  set requiredControls(value) {
    if (value) {
      this.button.classList.add(MediaControl.selectors.requiredClass);
    } else {
      this.button.classList.remove(MediaControl.selectors.requiredClass);
    }
  }

  /// If the video is contained in a Swiper slide, returns whether that slide is currently visible
  get swiperSlideIsVisible() {
    if (!this.isSwiperSlide) {
      return true;
    }

    const slide = this.video.closest('.swiper-slide');

    const slideVisibleClasses = [
      'swiper-slide-visible',
      'swiper-slide-active',
    ];

    return slideVisibleClasses.some((className) => slide?.classList.contains(className));
  }

  /**
   * Constructs a MediaControl instance given a button and its associated video
   * @param {HTMLButtonElement} button
   * @param {HTMLVideoElement} video
   */
  constructor(button, video, puppets = []) {
    this.button = button;
    this.video = video;
    this.puppets = puppets;
    this.allVideos = [this.video, ...this.puppets];
    this.label = this.button.querySelector(`.${MediaControl.selectors.labelClass}`);
    this.observerTarget = document.getElementById(this.button.dataset.observerTarget) ?? this.video;
    this.observerTarget.observedVideo = this.video;
    this.automaticPlaybackDisabled = MediaControl.automaticPlaybackDisabled;

    this.loop = this.video.loop;
    this.autoplay = this.video.autoplay;
    this.canPlay = this.videoCanPlay();
    this.requiredControls = false;
    this.userPaused = false;
    this.userPlay = false;
    this.forceAccessibleControls = this.button.dataset.forceAccessibleControls === 'true';

    this.isSwiperSlide = video.closest('.swiper-slide');

    if (!this.video.id) {
      this.video.id = crypto.randomUUID();
    }

    this.button.setAttribute('aria-controls', this.video.id);

    if (this.loop || this.forceAccessibleControls) {
      this.requiredControls = true;
    }

    this.registerEventListeners();

    /* eslint-disable no-param-reassign */
    this.allVideos.forEach((v) => {
      v.removeAttribute('autoplay');

      if (this.autoplay) {
        const resumeAutoplay = () => {
          if (v === this.video) {
            this.playIfAble();
          }
        };

        if (v === this.video) {
          if (v.paused) {
            if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              resumeAutoplay();
            } else {
              v.addEventListener('canplay', resumeAutoplay, { once: true });
              v.addEventListener('loadeddata', resumeAutoplay, { once: true });
              // preload="none" does not fetch until load()/play(); kick off for autoplay.
              if (v.readyState === HTMLMediaElement.HAVE_NOTHING) {
                v.load();
              }
            }
          }
        } else if (v.readyState === HTMLMediaElement.HAVE_NOTHING) {
          v.load();
        }
      }

      // Always show a poster fallback when autoplay is blocked (common on mobile/low-power).
      if (this.automaticPlaybackDisabled) {
        this.createPictureFallback(v);
      }
    });
    /* eslint-enable no-param-reassign */

    if (this.automaticPlaybackDisabled) {
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
    }

    if (!this.video.paused && !this.video.ended) {
      this.toggleState('pause');
    }

    this.button.mediaControl = this;
    this.allVideos.forEach((v) => {
      /* eslint-disable-next-line no-param-reassign */
      v.mediaControl = this;
    });
  }

  /* eslint-disable class-methods-use-this, no-param-reassign */
  createPictureFallback = (video) => {
    let picture = video.querySelector('picture');
    if (!picture && video.getAttribute('poster')) {
      const img = document.createElement('img');
      img.src = video.poster;

      picture = document.createElement('picture');
      picture.appendChild(img);
    }

    if (picture) {
      video.style.display = 'none';
      video.after(picture);
    }
  };

  removePictureFallback = (video) => {
    const picture = video.nextElementSibling;
    if (picture && picture.tagName === 'PICTURE') {
      picture.remove();
    }
    video.style.display = '';
  };
  /* eslint-enable class-methods-use-this, no-param-reassign */

  /**
   * Pauses the video
   */
  pause = () => {
    if (!this.video.paused) {
      this.video.pause();
    }
  };

  /**
   * Play's the video only if the following criteria is met:
   * - Video is paused
   * - Video is NOT ended
   * - Video source can play
   * - Video was NOT paused by the user
   *
   * And EITHER of these are true:
   * - Video is set to autoplay and reduced motion is NOT enabled
   * - Video playback was initiated by the user
   */
  playIfAble = async () => {
    const { reducedMotion } = MediaControl;

    const {
      ended,
      paused,
    } = this.video;

    const {
      automaticPlaybackDisabled,
      autoplay,
      canPlay,
      userPaused,
      userPlay,
    } = this;

    if (
      !automaticPlaybackDisabled
      && paused
      && !ended
      && canPlay
      && !userPaused
      && (
        (
          autoplay
          && !reducedMotion
        ) || userPlay
      )
    ) {
      try {
        await this.video.play();
      } catch (error) {
        if (!MediaControl.shouldIgnorePlayError(error)) {
          console.warn('Playback failed', error.message);
        }
      }
    }
  };

  /**
   * Updates traits that rely on the video's metadata being available.
   */
  afterVideoMetaLoaded = () => {
    if (this.video.duration > MediaControl.a11yThreshold || this.forceAccessibleControls) {
      this.requiredControls = true;
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
    }
  };

  /**
   * Registers video, button and media query listeners.
   */
  registerEventListeners() {
    if (this.canPlay) {
      if (this.loop || !this.autoplay || MediaControl.reducedMotion || this.forceAccessibleControls) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
      }

      if (this.video.readyState < HTMLMediaElement.HAVE_METADATA) {
        this.video.addEventListener('loadedmetadata', this.afterVideoMetaLoaded);
      } else {
        this.afterVideoMetaLoaded();
      }
    }

    this.video.addEventListener('ended', () => {
      this.userPaused = false;
      this.userPlay = false;

      if (this.loop) {
        return;
      }

      // Keep control visible for replay (click handler already handles ended).
      this.toggleState('play');
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
      this.button.classList.remove(MediaControl.selectors.fadedClass);
    });

    this.video.addEventListener('play', () => {
      /* eslint-disable no-param-reassign */
      if (this.automaticPlaybackDisabled) {
        this.allVideos.forEach(this.removePictureFallback);
      }
      /* eslint-enable no-param-reassign */

      this.toggleState('pause');
      this.userPaused = false;
      if (this.video.duration > MediaControl.a11yThreshold || this.loop || this.forceAccessibleControls) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
        this.button.classList.remove(MediaControl.selectors.fadedClass);
      }

      this.puppets.forEach(async (puppet) => {
        if (!puppet.ended) {
          try {
            await puppet.play();
          } catch (error) {
            if (!MediaControl.shouldIgnorePlayError(error)) {
              console.warn('Playback failed', error.message);
            }
          }
        }
      });
    });

    this.video.addEventListener('pause', () => {
      if (!this.video.ended) {
        this.toggleState('play');
      }

      this.puppets.forEach((puppet) => {
        puppet.pause();
      });
    });

    function preventBubbling(event) {
      event.stopPropagation();
    }

    this.button.addEventListener('pointerdown', preventBubbling);
    this.button.addEventListener('pointermove', preventBubbling);
    this.button.addEventListener('pointercancel', preventBubbling);
    this.button.addEventListener('pointerup', preventBubbling);

    this.button.addEventListener('click', async (event) => {
      event.stopPropagation();

      if (this.video.paused || this.video.ended) {
        if (this.canPlay) {
          try {
            await this.video.play();
            MediaControl.automaticPlaybackDisabled = false;
            this.automaticPlaybackDisabled = false;
            this.userPlay = true;
          } catch (error) {
            if (!MediaControl.shouldIgnorePlayError(error)) {
              console.warn('Playback failed', error.message);
            }
          }
        }
      } else {
        this.video.pause();
        this.userPaused = true;
      }
    });

    MediaControl.reducedMotionMQ.addEventListener('change', async (event) => {
      if (event.matches) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
        this.button.classList.remove(MediaControl.selectors.fadedClass);
        this.userPlay = false;
        this.userPause = false;
        this.toggleState('play');
        this.allVideos.forEach((video) => {
          video.load();
        });
      } else if (
        this.canPlay
        && this.isIntersecting
      ) {
        if (this.autoplay) {
          try {
            await this.video.play();
          } catch (error) {
            if (!MediaControl.shouldIgnorePlayError(error)) {
              console.warn('Playback failed', error.message);
            }
          }
        } else {
          this.button.classList.remove(MediaControl.selectors.hiddenClass);
          this.button.classList.remove(MediaControl.selectors.fadedClass);
          this.allVideos.forEach((video) => {
            video.load();
          });
        }
      }
    });
  }

  /**
   * Toggles the button state and sets the label text
   * @param {String} state - The state to show, either `play` or `pause`
   */
  toggleState(state) {
    switch (state) {
      case 'play':
        this.button.ariaPressed = false;
        if (this.label) {
          this.label.innerHTML = this.button.dataset.labelPlay;
        }
        break;
      case 'pause':
        this.button.ariaPressed = true;
        if (this.label) {
          this.label.innerHTML = this.button.dataset.labelPause;
        }
        break;
      default:
        console.warn('`toggleState` only accepts "play" and "pause" as values');
        break;
    }
  }

  /**
   * Checks whether at least one source element can by played by the browser. If
   * no source elements are present, tries to approximate using video.src.
   * @param {HTMLVideoElement} video
   */
  videoCanPlay() {
    const sources = this.video.querySelectorAll(':scope > source');
    if (sources.length) {
      return [...sources]
        .some((source) => {
          if (source.type) {
            return this.video.canPlayType(source.type) !== '';
          }

          if (source.src) {
            const mime = MediaControl.guessMimeFromFilename(source.src);
            if (mime) {
              return this.video.canPlayType(mime) !== '';
            }
          }

          return false;
        });
    }

    if (this.video.src) {
      const mime = MediaControl.guessMimeFromFilename(this.video.src);
      if (mime) {
        return this.video.canPlayType(mime) !== '';
      }
    }

    return true;
  }
}
